import { LISTING_MEDIA_TABS } from '@/widgets/listingDetail/listingMediaTabs';
/**
 * Ошибка валидации конфигурации
 */
export class ConfigValidationError extends Error {
    constructor(message, field, value) {
        super(message);
        this.name = 'ConfigValidationError';
        this.field = field;
        this.value = value;
    }
}
/**
 * Валидация языковой конфигурации
 */
function validateLanguageConfig(lang, index) {
    const errors = [];
    const prefix = `common.languages[${index}]`;
    if (!lang || typeof lang !== 'object') {
        errors.push(`${prefix}: должен быть объектом`);
        return errors;
    }
    if (typeof lang.isoCode !== 'string' || !lang.isoCode.match(/^[a-z]{2}$/)) {
        errors.push(`${prefix}.isoCode: должен быть строкой в формате "en", "it", etc.`);
    }
    if (typeof lang.isoCode3 !== 'string' || !lang.isoCode3.match(/^[a-z]{3}$/i)) {
        errors.push(`${prefix}.isoCode3: должен быть строкой в формате "eng", "ita", etc.`);
    }
    if (typeof lang.fileName !== 'string' || !lang.fileName.trim()) {
        errors.push(`${prefix}.fileName: должен быть непустой строкой`);
    }
    if (typeof lang.caption !== 'string' || !lang.caption.trim()) {
        errors.push(`${prefix}.caption: должен быть непустой строкой`);
    }
    if (typeof lang.iconResourceName !== 'string' || !lang.iconResourceName.trim()) {
        errors.push(`${prefix}.iconResourceName: должен быть непустой строкой`);
    }
    return errors;
}
/**
 * Валидация формата валюты
 */
function validateCurrencyFormat(currency) {
    const errors = [];
    if (!currency || typeof currency !== 'object') {
        errors.push('common.currencyFormat: должен быть объектом');
        return errors;
    }
    if (typeof currency.currency !== 'string' || !currency.currency.match(/^[A-Z]{3}$/)) {
        errors.push('common.currencyFormat.currency: должен быть строкой в формате "USD", "EUR", etc.');
    }
    if (typeof currency.currencyPosition !== 'string' ||
        !['left', 'right'].includes(currency.currencyPosition)) {
        errors.push('common.currencyFormat.currencyPosition: должен быть "left" или "right"');
    }
    if (typeof currency.hasCurrencySeparator !== 'boolean') {
        errors.push('common.currencyFormat.hasCurrencySeparator: должен быть boolean');
    }
    return errors;
}
/**
 * Валидация описания даты
 */
function validateDateDescription(date) {
    const errors = [];
    if (!date || typeof date !== 'object') {
        errors.push('common.dateDescription: должен быть объектом');
        return errors;
    }
    if (typeof date.firstDayOfWeek !== 'number' ||
        date.firstDayOfWeek < 0 || date.firstDayOfWeek > 6) {
        errors.push('common.dateDescription.firstDayOfWeek: должен быть числом от 0 до 6');
    }
    if (typeof date.dateFormat !== 'number') {
        errors.push('common.dateDescription.dateFormat: должен быть числом');
    }
    if (typeof date.dateSeparator !== 'string' || !date.dateSeparator.trim()) {
        errors.push('common.dateDescription.dateSeparator: должен быть непустой строкой');
    }
    return errors;
}
/**
 * Валидация мультиязычной строки
 */
function validateMultiLanguageString(value, fieldPath) {
    const errors = [];
    if (typeof value === 'string') {
        if (!value.trim()) {
            errors.push(`${fieldPath}: строка не может быть пустой`);
        }
        return errors;
    }
    if (!value || typeof value !== 'object') {
        errors.push(`${fieldPath}: должен быть строкой или объектом`);
        return errors;
    }
    const keys = Object.keys(value);
    if (keys.length === 0) {
        errors.push(`${fieldPath}: объект не может быть пустым`);
        return errors;
    }
    for (const [langCode, text] of Object.entries(value)) {
        if (typeof text !== 'string' || !text.toString().trim()) {
            errors.push(`${fieldPath}.${langCode}: должен быть непустой строкой`);
        }
    }
    return errors;
}
/**
 * Валидация конфигурации ссылки
 */
function validateLinkConfig(link, index) {
    const errors = [];
    const prefix = `application.header.links[${index}]`;
    if (!link || typeof link !== 'object') {
        errors.push(`${prefix}: должен быть объектом`);
        return errors;
    }
    if (typeof link.path !== 'string' || !link.path.startsWith('/')) {
        errors.push(`${prefix}.path: должен быть строкой, начинающейся с "/"`);
    }
    errors.push(...validateMultiLanguageString(link.caption, `${prefix}.caption`));
    if (typeof link.iconAlias !== 'string' || !link.iconAlias.trim()) {
        errors.push(`${prefix}.iconAlias: должен быть непустой строкой`);
    }
    return errors;
}
function isAllowedLinkPath(value) {
    if (typeof value !== 'string' || !value.trim()) {
        return false;
    }
    return value.startsWith('/') || /^(https?:\/\/|mailto:|tel:)/i.test(value);
}
function validateContentLink(link, fieldPath) {
    const errors = [];
    if (!link || typeof link !== 'object') {
        errors.push(`${fieldPath}: должен быть объектом`);
        return errors;
    }
    if (!isAllowedLinkPath(link.link)) {
        errors.push(`${fieldPath}.link: должен быть внутренним путём или абсолютной ссылкой`);
    }
    if (link.caption !== undefined) {
        errors.push(...validateMultiLanguageString(link.caption, `${fieldPath}.caption`));
    }
    if (link.linkCaption !== undefined) {
        errors.push(...validateMultiLanguageString(link.linkCaption, `${fieldPath}.linkCaption`));
    }
    if (link.newTab !== undefined && typeof link.newTab !== 'boolean') {
        errors.push(`${fieldPath}.newTab: должен быть boolean`);
    }
    return errors;
}
function validateHomeFallbackListing(item, fieldPath) {
    const errors = [];
    if (!item || typeof item !== 'object') {
        errors.push(`${fieldPath}: должен быть объектом`);
        return errors;
    }
    if (typeof item.slug !== 'string' || !item.slug.trim()) {
        errors.push(`${fieldPath}.slug: должен быть непустой строкой`);
    }
    errors.push(...validateMultiLanguageString(item.title, `${fieldPath}.title`));
    if (item.summary !== undefined) {
        errors.push(...validateMultiLanguageString(item.summary, `${fieldPath}.summary`));
    }
    if (item.displayAddress !== undefined) {
        errors.push(...validateMultiLanguageString(item.displayAddress, `${fieldPath}.displayAddress`));
    }
    if (item.propertyType !== undefined) {
        errors.push(...validateMultiLanguageString(item.propertyType, `${fieldPath}.propertyType`));
    }
    if (item.coverImageUrl !== undefined && (typeof item.coverImageUrl !== 'string' || !item.coverImageUrl.trim())) {
        errors.push(`${fieldPath}.coverImageUrl: должен быть непустой строкой`);
    }
    return errors;
}
function validateHomeListingsSection(section, fieldPath) {
    const errors = [];
    if (!section || typeof section !== 'object') {
        errors.push(`${fieldPath}: должен быть объектом`);
        return errors;
    }
    errors.push(...validateMultiLanguageString(section.title, `${fieldPath}.title`));
    if (section.cta !== undefined) {
        errors.push(...validateContentLink(section.cta, `${fieldPath}.cta`));
    }
    if (section.limit !== undefined && (typeof section.limit !== 'number' || section.limit < 1)) {
        errors.push(`${fieldPath}.limit: должен быть числом >= 1`);
    }
    if (section.sort !== undefined && typeof section.sort !== 'string') {
        errors.push(`${fieldPath}.sort: должен быть строкой`);
    }
    if (section.useApi !== undefined && typeof section.useApi !== 'boolean') {
        errors.push(`${fieldPath}.useApi: должен быть boolean`);
    }
    if (section.emptyText !== undefined) {
        errors.push(...validateMultiLanguageString(section.emptyText, `${fieldPath}.emptyText`));
    }
    if (section.errorText !== undefined) {
        errors.push(...validateMultiLanguageString(section.errorText, `${fieldPath}.errorText`));
    }
    if (section.fallbackListings !== undefined) {
        if (!Array.isArray(section.fallbackListings)) {
            errors.push(`${fieldPath}.fallbackListings: должен быть массивом`);
        }
        else {
            section.fallbackListings.forEach((item, index) => {
                errors.push(...validateHomeFallbackListing(item, `${fieldPath}.fallbackListings[${index}]`));
            });
        }
    }
    return errors;
}
function validateHomeWidgetContent(content, fieldPath) {
    const errors = [];
    if (!content || typeof content !== 'object') {
        errors.push(`${fieldPath}: должен быть объектом`);
        return errors;
    }
    if (content.startSection !== undefined) {
        if (!content.startSection || typeof content.startSection !== 'object') {
            errors.push(`${fieldPath}.startSection: должен быть объектом`);
        }
        else {
            errors.push(...validateMultiLanguageString(content.startSection.title, `${fieldPath}.startSection.title`));
            errors.push(...validateMultiLanguageString(content.startSection.description, `${fieldPath}.startSection.description`));
            errors.push(...validateContentLink(content.startSection.button, `${fieldPath}.startSection.button`));
        }
    }
    if (!content.hero || typeof content.hero !== 'object') {
        errors.push(`${fieldPath}.hero: обязателен и должен быть объектом`);
    }
    else {
        errors.push(...validateMultiLanguageString(content.hero.headline, `${fieldPath}.hero.headline`));
        if (content.hero.kicker !== undefined) {
            errors.push(...validateMultiLanguageString(content.hero.kicker, `${fieldPath}.hero.kicker`));
        }
        if (content.hero.subheadline !== undefined) {
            errors.push(...validateMultiLanguageString(content.hero.subheadline, `${fieldPath}.hero.subheadline`));
        }
        if (content.hero.primaryCta !== undefined) {
            errors.push(...validateContentLink(content.hero.primaryCta, `${fieldPath}.hero.primaryCta`));
        }
        if (content.hero.spotlight !== undefined) {
            if (!content.hero.spotlight || typeof content.hero.spotlight !== 'object') {
                errors.push(`${fieldPath}.hero.spotlight: должен быть объектом`);
            }
            else {
                errors.push(...validateMultiLanguageString(content.hero.spotlight.title, `${fieldPath}.hero.spotlight.title`));
                errors.push(...validateContentLink(content.hero.spotlight, `${fieldPath}.hero.spotlight`));
            }
        }
    }
    errors.push(...validateHomeListingsSection(content.featuredSales, `${fieldPath}.featuredSales`));
    if (!content.valuationCta || typeof content.valuationCta !== 'object') {
        errors.push(`${fieldPath}.valuationCta: обязателен и должен быть объектом`);
    }
    else {
        errors.push(...validateMultiLanguageString(content.valuationCta.quote, `${fieldPath}.valuationCta.quote`));
        if (content.valuationCta.source !== undefined) {
            errors.push(...validateMultiLanguageString(content.valuationCta.source, `${fieldPath}.valuationCta.source`));
        }
        if (content.valuationCta.cta !== undefined) {
            errors.push(...validateContentLink(content.valuationCta.cta, `${fieldPath}.valuationCta.cta`));
        }
    }
    if (!content.testimonial || typeof content.testimonial !== 'object') {
        errors.push(`${fieldPath}.testimonial: обязателен и должен быть объектом`);
    }
    else {
        errors.push(...validateMultiLanguageString(content.testimonial.quote, `${fieldPath}.testimonial.quote`));
        errors.push(...validateMultiLanguageString(content.testimonial.author, `${fieldPath}.testimonial.author`));
        if (content.testimonial.meta !== undefined) {
            errors.push(...validateMultiLanguageString(content.testimonial.meta, `${fieldPath}.testimonial.meta`));
        }
        if (content.testimonial.secondary !== undefined) {
            if (!content.testimonial.secondary || typeof content.testimonial.secondary !== 'object') {
                errors.push(`${fieldPath}.testimonial.secondary: должен быть объектом`);
            }
            else {
                errors.push(...validateMultiLanguageString(content.testimonial.secondary.quote, `${fieldPath}.testimonial.secondary.quote`));
                errors.push(...validateMultiLanguageString(content.testimonial.secondary.author, `${fieldPath}.testimonial.secondary.author`));
                if (content.testimonial.secondary.meta !== undefined) {
                    errors.push(...validateMultiLanguageString(content.testimonial.secondary.meta, `${fieldPath}.testimonial.secondary.meta`));
                }
            }
        }
    }
    if (!content.aboutEditorial || typeof content.aboutEditorial !== 'object') {
        errors.push(`${fieldPath}.aboutEditorial: обязателен и должен быть объектом`);
    }
    else {
        errors.push(...validateMultiLanguageString(content.aboutEditorial.quote, `${fieldPath}.aboutEditorial.quote`));
        if (content.aboutEditorial.source !== undefined) {
            errors.push(...validateMultiLanguageString(content.aboutEditorial.source, `${fieldPath}.aboutEditorial.source`));
        }
        if (content.aboutEditorial.cta !== undefined) {
            errors.push(...validateContentLink(content.aboutEditorial.cta, `${fieldPath}.aboutEditorial.cta`));
        }
    }
    errors.push(...validateHomeListingsSection(content.pastSales, `${fieldPath}.pastSales`));
    if (content.pastSales && content.pastSales.mode !== undefined && !['api', 'fallback'].includes(content.pastSales.mode)) {
        errors.push(`${fieldPath}.pastSales.mode: должен быть "api" или "fallback"`);
    }
    return errors;
}
function validateFooterConfig(footer) {
    const errors = [];
    if (!footer || typeof footer !== 'object') {
        errors.push('application.footer: должен быть объектом');
        return errors;
    }
    const validateFooterColumn = (column, columnPath, requireLinks = true) => {
        if (!column || typeof column !== 'object') {
            errors.push(`${columnPath}: должен быть объектом`);
            return;
        }
        errors.push(...validateMultiLanguageString(column.title, `${columnPath}.title`));
        if (column.links !== undefined || requireLinks) {
            if (!Array.isArray(column.links) || (requireLinks && column.links.length === 0)) {
                errors.push(`${columnPath}.links: должен быть непустым массивом`);
            }
            else {
                column.links.forEach((link, index) => {
                    errors.push(...validateContentLink(link, `${columnPath}.links[${index}]`));
                });
            }
        }
    };
    validateFooterColumn(footer.about, 'application.footer.about');
    if (!footer.contact || typeof footer.contact !== 'object') {
        errors.push('application.footer.contact: должен быть объектом');
    }
    else {
        errors.push(...validateMultiLanguageString(footer.contact.title, 'application.footer.contact.title'));
        if (footer.contact.address !== undefined) {
            errors.push(...validateMultiLanguageString(footer.contact.address, 'application.footer.contact.address'));
        }
        if (footer.contact.links !== undefined) {
            if (!Array.isArray(footer.contact.links)) {
                errors.push('application.footer.contact.links: должен быть массивом');
            }
            else {
                footer.contact.links.forEach((link, index) => {
                    errors.push(...validateContentLink(link, `application.footer.contact.links[${index}]`));
                });
            }
        }
    }
    if (!footer.newsletter || typeof footer.newsletter !== 'object') {
        errors.push('application.footer.newsletter: должен быть объектом');
    }
    else {
        errors.push(...validateMultiLanguageString(footer.newsletter.title, 'application.footer.newsletter.title'));
        errors.push(...validateMultiLanguageString(footer.newsletter.inputPlaceholder, 'application.footer.newsletter.inputPlaceholder'));
        errors.push(...validateMultiLanguageString(footer.newsletter.buttonCaption, 'application.footer.newsletter.buttonCaption'));
        if (footer.newsletter.description !== undefined) {
            errors.push(...validateMultiLanguageString(footer.newsletter.description, 'application.footer.newsletter.description'));
        }
        if (footer.newsletter.consentLabel !== undefined) {
            errors.push(...validateMultiLanguageString(footer.newsletter.consentLabel, 'application.footer.newsletter.consentLabel'));
        }
    }
    validateFooterColumn(footer.social, 'application.footer.social');
    validateFooterColumn(footer.legal, 'application.footer.legal');
    if (footer.copyright !== undefined) {
        errors.push(...validateMultiLanguageString(footer.copyright, 'application.footer.copyright'));
    }
    return errors;
}
function validateRoutingConfig(routing) {
    const errors = [];
    if (!routing || typeof routing !== 'object') {
        errors.push('application.routing: должен быть объектом');
        return errors;
    }
    if (!Array.isArray(routing.routes) || routing.routes.length === 0) {
        errors.push('application.routing.routes: должен быть непустым массивом');
        return errors;
    }
    routing.routes.forEach((route, index) => {
        const prefix = `application.routing.routes[${index}]`;
        if (!route || typeof route !== 'object') {
            errors.push(`${prefix}: должен быть объектом`);
            return;
        }
        if (typeof route.path !== 'string' || !route.path.startsWith('/')) {
            errors.push(`${prefix}.path: должен быть строкой, начинающейся с "/"`);
        }
        if (!route.widget || typeof route.widget !== 'object') {
            errors.push(`${prefix}.widget: должен быть объектом`);
            return;
        }
        if (typeof route.widget.className !== 'string' || !route.widget.className.trim()) {
            errors.push(`${prefix}.widget.className: должен быть непустой строкой`);
        }
        if (route.widget.className === 'widget.home') {
            errors.push(...validateHomeWidgetContent(route.widget.content, `${prefix}.widget.content`));
        }
        if (route.widget.className === 'widget.listing.detail') {
            errors.push(...validateListingDetailWidgetContent(route.widget.content, `${prefix}.widget.content`));
        }
    });
    return errors;
}
function validateListingDetailWidgetContent(content, fieldPath) {
    const errors = [];
    if (content === undefined) {
        return errors;
    }
    if (!content || typeof content !== 'object' || Array.isArray(content)) {
        errors.push(`${fieldPath}: должен быть объектом`);
        return errors;
    }
    if (content.mediaTabs === undefined) {
        return errors;
    }
    if (!Array.isArray(content.mediaTabs)) {
        errors.push(`${fieldPath}.mediaTabs: должен быть массивом`);
        return errors;
    }
    const seen = new Set();
    const allowedTabs = new Set(LISTING_MEDIA_TABS);
    content.mediaTabs.forEach((tab, index) => {
        if (typeof tab !== 'string' || !allowedTabs.has(tab)) {
            errors.push(`${fieldPath}.mediaTabs[${index}]: должен быть одним из ${LISTING_MEDIA_TABS.join(', ')}`);
            return;
        }
        if (seen.has(tab)) {
            errors.push(`${fieldPath}.mediaTabs: не должен содержать дублирующиеся значения (${tab})`);
            return;
        }
        seen.add(tab);
    });
    return errors;
}
/**
 * Строгая валидация конфигурации
 * Выбрасывает исключение при первой ошибке
 */
export function validateConfig(config) {
    if (!config || typeof config !== 'object') {
        throw new ConfigValidationError('Конфиг должен быть объектом');
    }
    const cfg = config;
    const errors = [];
    // Валидация основных полей
    if (typeof cfg.agencyAlias !== 'string' || !cfg.agencyAlias.trim()) {
        errors.push('agencyAlias: должен быть непустой строкой');
    }
    else if (!cfg.agencyAlias.match(/^[a-z0-9-]+$/)) {
        errors.push('agencyAlias: должен содержать только строчные буквы, цифры и дефисы');
    }
    if (typeof cfg.version !== 'number' || cfg.version < 1) {
        errors.push('version: должен быть положительным числом');
    }
    // Валидация common секции
    if (!cfg.common || typeof cfg.common !== 'object') {
        errors.push('common: обязательная секция, должна быть объектом');
    }
    else {
        const common = cfg.common;
        // Языки
        if (!Array.isArray(common.languages)) {
            errors.push('common.languages: должен быть массивом');
        }
        else if (common.languages.length === 0) {
            errors.push('common.languages: должен содержать хотя бы один язык');
        }
        else {
            common.languages.forEach((lang, index) => {
                errors.push(...validateLanguageConfig(lang, index));
            });
            // Проверка уникальности кодов языков
            const isoCodes = common.languages.map((l) => l.isoCode).filter(Boolean);
            const duplicates = isoCodes.filter((code, index) => isoCodes.indexOf(code) !== index);
            if (duplicates.length > 0) {
                errors.push(`common.languages: дублирующиеся коды языков: ${duplicates.join(', ')}`);
            }
        }
        // Валюта
        errors.push(...validateCurrencyFormat(common.currencyFormat));
        // Дата
        errors.push(...validateDateDescription(common.dateDescription));
        // Телефонные форматы
        if (!Array.isArray(common.phoneFormats)) {
            errors.push('common.phoneFormats: должен быть массивом');
        }
        else {
            common.phoneFormats.forEach((phone, index) => {
                const prefix = `common.phoneFormats[${index}]`;
                if (!phone || typeof phone !== 'object') {
                    errors.push(`${prefix}: должен быть объектом`);
                }
                else {
                    if (typeof phone.code !== 'string' || !phone.code.startsWith('+')) {
                        errors.push(`${prefix}.code: должен быть строкой, начинающейся с "+"`);
                    }
                    if (typeof phone.phoneMinLength !== 'number' || phone.phoneMinLength < 1) {
                        errors.push(`${prefix}.phoneMinLength: должен быть положительным числом`);
                    }
                    if (typeof phone.phoneMaxLength !== 'number' || phone.phoneMaxLength < phone.phoneMinLength) {
                        errors.push(`${prefix}.phoneMaxLength: должен быть числом >= phoneMinLength`);
                    }
                }
            });
        }
    }
    // Валидация application секции
    if (!cfg.application || typeof cfg.application !== 'object') {
        errors.push('application: обязательная секция, должна быть объектом');
    }
    else {
        const app = cfg.application;
        // Info
        if (!app.info || typeof app.info !== 'object') {
            errors.push('application.info: должен быть объектом');
        }
        else {
            if (typeof app.info.caption !== 'string' || !app.info.caption.trim()) {
                errors.push('application.info.caption: должен быть непустой строкой');
            }
            if (typeof app.info.copyright !== 'string' || !app.info.copyright.trim()) {
                errors.push('application.info.copyright: должен быть непустой строкой');
            }
        }
        // Contact (optional)
        if (app.contact !== undefined) {
            if (!app.contact || typeof app.contact !== 'object') {
                errors.push('application.contact: должен быть объектом');
            }
            else {
                if (app.contact.whatsappPhone !== undefined) {
                    if (typeof app.contact.whatsappPhone !== 'string' || !app.contact.whatsappPhone.trim()) {
                        errors.push('application.contact.whatsappPhone: должен быть непустой строкой');
                    }
                }
                if (app.contact.showWhatsAppCta !== undefined && typeof app.contact.showWhatsAppCta !== 'boolean') {
                    errors.push('application.contact.showWhatsAppCta: должен быть boolean');
                }
                if (app.contact.agencyName !== undefined) {
                    errors.push(...validateMultiLanguageString(app.contact.agencyName, 'application.contact.agencyName'));
                }
            }
        }
        // Icons
        if (!app.icons || typeof app.icons !== 'object') {
            errors.push('application.icons: должен быть объектом');
        }
        else {
            const requiredIcons = ['logo32x32', 'desktopHeaderLogo', 'mobileHeaderLogo'];
            requiredIcons.forEach(iconKey => {
                if (typeof app.icons[iconKey] !== 'string' || !app.icons[iconKey].trim()) {
                    errors.push(`application.icons.${iconKey}: должен быть непустой строкой`);
                }
            });
        }
        // Links
        if (!app.links || typeof app.links !== 'object') {
            errors.push('application.links: должен быть объектом');
        }
        else {
            const requiredLinks = ['home', 'listings', 'listingsBuy', 'listingsRent', 'listingsSell', 'agencies', 'company'];
            requiredLinks.forEach(linkKey => {
                if (typeof app.links[linkKey] !== 'string' || !app.links[linkKey].startsWith('/')) {
                    errors.push(`application.links.${linkKey}: должен быть строкой, начинающейся с "/"`);
                }
            });
        }
        // Header
        if (!app.header || typeof app.header !== 'object') {
            errors.push('application.header: должен быть объектом');
        }
        else {
            if (!Array.isArray(app.header.links)) {
                errors.push('application.header.links: должен быть массивом');
            }
            else if (app.header.links.length === 0) {
                errors.push('application.header.links: должен содержать хотя бы одну ссылку');
            }
            else {
                app.header.links.forEach((link, index) => {
                    errors.push(...validateLinkConfig(link, index));
                });
            }
        }
        errors.push(...validateFooterConfig(app.footer));
        if (!app.routing) {
            errors.push('application.routing: обязательная секция, должна быть объектом');
        }
        else {
            errors.push(...validateRoutingConfig(app.routing));
        }
    }
    // Если есть ошибки - выбрасываем исключение
    if (errors.length > 0) {
        const message = `Ошибки валидации конфига:\n${errors.map(e => `  - ${e}`).join('\n')}`;
        throw new ConfigValidationError(message);
    }
    return cfg;
}
/**
 * Мягкая валидация конфига (возвращает результат вместо исключения)
 */
export function validateConfigSoft(config) {
    try {
        validateConfig(config);
        return {
            isValid: true,
            errors: [],
            warnings: []
        };
    }
    catch (error) {
        if (error instanceof ConfigValidationError) {
            return {
                isValid: false,
                errors: [error.message],
                warnings: []
            };
        }
        return {
            isValid: false,
            errors: [`Неожиданная ошибка валидации: ${error}`],
            warnings: []
        };
    }
}
/**
 * Проверка, является ли конфиг валидным (boolean результат)
 */
export function isValidConfig(config) {
    try {
        validateConfig(config);
        return true;
    }
    catch {
        return false;
    }
}
